import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Card,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import Label from '../../../components/Label';
import { safeJsonParse } from '../../../admin/api';
import { fDateTime } from '../../../utils/formatTime';
import DetailRow from '../ManageUserDialog/DetailRow';

const POLICY_INSPECTOR_DRAWER_WIDTH = 720;

function toBool(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function getDecisionStatus(row) {
  if (toBool(row?.requires_review)) {
    return 'awaiting review';
  }

  if (toBool(row?.allowed)) {
    return 'resolved';
  }

  return 'failed';
}

function JsonPanel({ value, emptyText = 'No data available.' }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        borderRadius: 2,
        overflow: 'auto',
        fontSize: 12,
        lineHeight: 1.6,
        bgcolor: 'background.neutral',
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      {value ? JSON.stringify(value, null, 2) : emptyText}
    </Box>
  );
}

JsonPanel.propTypes = {
  value: PropTypes.any,
  emptyText: PropTypes.string,
};

export default function PolicyInspectorDrawer({ open, onClose, row }) {
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (open) {
      setTab(0);
    }
  }, [open]);

  const matchedRules = useMemo(() => safeJsonParse(row?.matched_rules, []), [row?.matched_rules]);
  const contextPayload = useMemo(() => safeJsonParse(row?.context_payload, {}), [row?.context_payload]);
  const allowed = toBool(row?.allowed);
  const requiresReview = toBool(row?.requires_review);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: POLICY_INSPECTOR_DRAWER_WIDTH },
        },
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ px: 3, py: 2.5 }}>
          <Stack spacing={1}>
            <Typography variant="h6">Policy Inspector</Typography>
            
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                Reference: {row?.reference || 'Selected decision row'}
              </Typography>
              {row ? (
                <Label color={requiresReview ? 'warning' : allowed ? 'success' : 'error'}>
                  {getDecisionStatus(row)}
                </Label>
              ) : null}
            </Stack>
          </Stack>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
          <Stack spacing={2.5}>
            {/* <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Decision
                  </Typography>
                  <Typography variant="subtitle1">{row?.decision_code || 'N/A'}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Scope
                  </Typography>
                  <Typography variant="subtitle1">{row?.decision_scope || 'N/A'}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="subtitle1">
                    {row?.created_at ? fDateTime(row.created_at) : 'N/A'}
                  </Typography>
                </Card>
              </Grid>
            </Grid> */}

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ px: 2, pt: 1 }}>
                <Tab label="Details" />
                <Tab label="Matching Rules" />
                <Tab label="Context Payload" />
              </Tabs>
              <Divider />

              <Box sx={{ p: 2 }}>
                {tab === 0 ? (
                  <Stack spacing={3}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1">Decision Detail</Typography>
                      <Divider />
                      <DetailRow label="Reference" value={row?.reference} />
                      <DetailRow label="Scope" value={row?.decision_scope} />
                      <DetailRow label="Decision code" value={row?.decision_code} />
                      <DetailRow label="Tier" value={row?.tier_code} />
                      <DetailRow label="Allowed" value={allowed ? 'Yes' : 'No'} />
                      <DetailRow label="Requires review" value={requiresReview ? 'Yes' : 'No'} />
                      <DetailRow label="Created at" value={row?.created_at ? fDateTime(row.created_at) : 'N/A'} />
                    </Stack>

                    <Stack spacing={2}>
                      <Typography variant="subtitle1">Selected Payload</Typography>
                      <Divider />
                      <Typography variant="body2" color="text.secondary">
                        Raw context payload returned for the selected decision row.
                      </Typography>
                      <JsonPanel value={contextPayload} emptyText="No context payload returned." />
                    </Stack>
                  </Stack>
                ) : tab === 1 ? (
                  matchedRules?.length ? (
                    <Stack spacing={1.5}>
                      {matchedRules.map((rule, index) => (
                        <Card
                          key={`${rule.tier_code || 'rule'}-${index}`}
                          variant="outlined"
                          sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                            <Typography variant="subtitle2">
                              {rule.tier_code || `Rule ${index + 1}`}
                            </Typography>
                            <Chip size="small" label={`Daily limit: ${rule.daily_limit ?? 'N/A'}`} />
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Observed: {rule.observed ?? 'N/A'} · Projected: {rule.projected ?? 'N/A'} · Completed refs:{' '}
                            {rule.counted_completed_references ?? 'N/A'}
                          </Typography>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No matched rules were returned for this decision.
                    </Typography>
                  )
                ) : (
                  <JsonPanel value={contextPayload} emptyText="No context payload returned." />
                )}
              </Box>
            </Card>
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
}

PolicyInspectorDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object,
};
